import { Injectable, inject } from '@angular/core';
import type { ProductFetchRequest } from '../observation/observation-acquisition.service';
import {
  ObservationRefreshService,
  type RefreshProgress,
} from '../observation/observation-refresh.service';
import {
  type CacheBatchResult,
  ObservationRepository,
} from '../observation/observation-repository';
import { monthsForDayRange } from '../observation/read-months';
import type { EAN } from '../shared/ean';
import { minusDays } from '../shared/iso-day';
import { LEAD_IN_DAYS } from './constants';
import { type BrandLedger, buildLedger, isIsoDay } from './ledger';

type LedgerRequest = {
  readonly brand: string;
  /** The brand's products, with the page the scraper needs when a row is stale. */
  readonly products: readonly ProductFetchRequest[];
  /** First visible day, YYYY-MM-DD. */
  readonly dateStart: string;
  /** YYYY-MM-DD. */
  readonly today: string;
  readonly signal: AbortSignal;
  /** False reads the rows the cache holds and requests no scrape; omitted or true refreshes stale rows first. */
  readonly refresh?: boolean;
  readonly onProgress?: (progress: RefreshProgress) => void | Promise<void>;
};

type LedgerResult = {
  readonly ledger: BrandLedger;
  /** Products enqueued for a scrape and still stale at the deadline; their rows are in the ledger as held. */
  readonly pending: readonly EAN[];
  /** Stale products without a page, so never enqueued. */
  readonly unscrapable: readonly EAN[];
};

type Held = { readonly fingerprint: string; readonly ledger: BrandLedger; readonly days: number };

// About 150 bytes a Day; the observation cache itself holds at most 500 000 observations.
const MAX_HELD_DAYS = 1_000_000;

function dayCount(ledger: BrandLedger): number {
  let count = 0;
  for (const byEan of ledger.series.values())
    for (const days of byEan.values()) count += days.length;
  return count;
}

// The rows' `upd` per EAN is the observation cache's generation for the brand: equal rows build an equal ledger.
function fingerprintOf(
  rows: CacheBatchResult,
  eans: readonly EAN[],
  dateStart: string,
  today: string
): string {
  const perEan = [...eans].sort().map((ean) => `${ean}=${rows.get(ean)?.upd ?? '-'}`);
  return `${dateStart}|${today}|${perEan.join(',')}`;
}

type InFlight = {
  readonly controller: AbortController;
  joined: number;
  readonly result: Promise<LedgerResult>;
};

// A caller shares the running build and leaves it on its own abort; the build itself is aborted
// only when every caller has left, so one reader's reload cannot fail another's read.
function join(entry: InFlight, signal: AbortSignal): Promise<LedgerResult> {
  if (signal.aborted) return Promise.reject(signal.reason);
  entry.joined++;
  return new Promise<LedgerResult>((resolve, reject) => {
    let left = false;
    const leave = () => {
      if (left) return;
      left = true;
      signal.removeEventListener('abort', onAbort);
      entry.joined--;
    };
    const onAbort = () => {
      leave();
      if (entry.joined === 0) entry.controller.abort(signal.reason);
      reject(signal.reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
    entry.result.then(
      (result) => {
        leave();
        resolve(result);
      },
      (cause: unknown) => {
        leave();
        reject(cause);
      }
    );
  });
}

// The same brand, EAN set, dates and read mode asked twice while the first read runs is one request.
function requestKey({ brand, products, dateStart, today, refresh = true }: LedgerRequest): string {
  const eans = [...new Set(products.map(({ ean }) => ean))].sort();
  return `${brand}|${dateStart}|${today}|${refresh}|${eans.join(',')}`;
}

/**
 * One brand's campaign ledger, computed once from the observation refresh and reused by every
 * reader while the brand's rows, EAN set and dates are unchanged (CONTEXT.md, Campaign ledger).
 *
 * The held ledgers live as long as the injector holding `portalDomainProviders`: the root
 * injector today, the lazy portal route once the portal boundary moves them there, after which
 * they die on leaving the portal. The service keeps no module-level state (ADR 0002).
 */
@Injectable()
export class CampaignLedgerService {
  private readonly refresh = inject(ObservationRefreshService);
  private readonly repo = inject(ObservationRepository);
  private readonly held = new Map<string, Held>();
  private readonly inFlight = new Map<string, InFlight>();

  /**
   * Reads the visible range plus the lead-in, month-rounded, through the observation refresh (or
   * the repository alone when `refresh` is false) and builds the brand's ledger from every row
   * returned. Rejects with `signal.reason` on abort. Concurrent identical requests share one
   * read and one build.
   */
  forBrand(request: LedgerRequest): Promise<LedgerResult> {
    const key = requestKey(request);
    let entry = this.inFlight.get(key);
    if (!entry || entry.controller.signal.aborted) {
      const controller = new AbortController();
      const started: InFlight = {
        controller,
        joined: 0,
        result: this.build({ ...request, signal: controller.signal }),
      };
      const forget = () => {
        if (this.inFlight.get(key) === started) this.inFlight.delete(key);
      };
      // An abandoned build leaves the map at once, before it settles, so the next caller starts
      // its own instead of inheriting the abort.
      controller.signal.addEventListener('abort', forget, { once: true });
      started.result.catch(() => undefined).finally(forget);
      this.inFlight.set(key, started);
      entry = started;
    }
    return join(entry, request.signal);
  }

  private async build(request: LedgerRequest): Promise<LedgerResult> {
    const { brand, products, dateStart, today, signal, refresh = true, onProgress } = request;
    const empty = (): LedgerResult => ({
      ledger: buildLedger({ skus: [], dateStart, today }),
      pending: [],
      unscrapable: [],
    });
    if (!isIsoDay(dateStart) || !isIsoDay(today)) return empty();
    const axisStart = minusDays(dateStart, LEAD_IN_DAYS);
    if (axisStart > today) return empty();

    const monthsYM = monthsForDayRange(axisStart, today);
    const eans = [...new Set(products.map(({ ean }) => ean))];
    const { rows, pending, unscrapable } = refresh
      ? await this.refresh.refresh(products, { signal, monthsYM, onProgress })
      : {
          rows: await this.repo.readBatch(eans, monthsYM, undefined, signal),
          pending: [],
          unscrapable: [],
        };
    const fingerprint = fingerprintOf(rows, eans, dateStart, today);
    const held = this.held.get(brand);
    if (held?.fingerprint === fingerprint) {
      // Re-insertion keeps the map in recency order for eviction.
      this.held.delete(brand);
      this.held.set(brand, held);
      return { ledger: held.ledger, pending, unscrapable };
    }

    const skus = eans
      .map((ean) => ({ ean, observations: rows.get(ean)?.observations ?? [] }))
      .filter((sku) => sku.observations.length > 0);
    const ledger = buildLedger({ skus, dateStart, today });
    this.held.delete(brand);
    this.held.set(brand, { fingerprint, ledger, days: dayCount(ledger) });
    this.evict();
    return { ledger, pending, unscrapable };
  }

  // Drops the least recently used brands until the held days fit, keeping the newest.
  private evict(): void {
    let total = 0;
    for (const { days } of this.held.values()) total += days;
    for (const [brand, { days }] of this.held) {
      if (total <= MAX_HELD_DAYS || this.held.size === 1) return;
      this.held.delete(brand);
      total -= days;
    }
  }
}
